<script lang="ts" setup>
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import PickUpPointNotification from '~/components/modal-windows/shipping/PickUpPointNotification.vue'
import constants from '~/constants'
import OrderShippingType from '~/enums/order-shipping-type'
import { useCartStore } from '~/store/cart'
import type { YandexDeliveryPickUpPointShippingData } from '~/types/shipping'

const model = defineModel<boolean>()

const props = defineProps<{
  declaredValueInKopecks: number
}>()

const messages = { failedToCalculateShippingCost: 'Не удалось рассчитать стоимость доставки.' }

const { $apiHelper, $toast } = useNuxtApp()

const cartStore = useCartStore()

const isGettingShippingCost = ref(false)
const scriptElement = ref<HTMLScriptElement | null>(null)
const shippingData = ref<YandexDeliveryPickUpPointShippingData | null>(null)

function close (): void {
  model.value = false
}

function closed (): void {
  (scriptElement.value as HTMLScriptElement).parentNode?.removeChild(scriptElement.value as HTMLScriptElement)
  document.removeEventListener('YaNddWidgetPointSelected', onPickUpPointSelected)
}

async function getShippingCost (): Promise<void> {
  isGettingShippingCost.value = true

  const { declaredValueInKopecks } = props
  const { pickUpPointId } = shippingData.value!
  const { failedToCalculateShippingCost } = messages

  try {
    const { meta } = await $apiHelper.actions.getShippingCost(
      OrderShippingType.YandexDeliveryPickUpPoint,
      cartStore.weightInGrams!,
      { declaredValueInKopecks, pickUpPointId }
    ) as any

    if (meta.errorMessage) {
      $toast.error(failedToCalculateShippingCost)
    } else {
      cartStore.shippingCostInKopecks = meta.shippingCostInKopecks
      cartStore.shippingData = shippingData.value

      close()
    }
  } catch (error) {
    console.error(error)

    $toast.error(failedToCalculateShippingCost)
  }

  isGettingShippingCost.value = false
}

async function onPickUpPointSelected (data: any): Promise<void> {
  console.log('onPickUpPointSelected', data)

  const {
    apartment, building, country, house, housing, locality, region, street, subRegion
  } = data.detail.address
  shippingData.value = {
    fullAddress: [country, region, subRegion, locality, street, house, housing, building, apartment]
      .filter((x: string): boolean => Boolean(x))
      .join(', '),
    pickUpPointId: data.detail.id
  }

  console.log(shippingData.value)

  await getShippingCost()
}

/**
 * @see https://yandex.ru/support2/delivery-profile/ru/modules/widgets
 */
function opened (): void {
  const _scriptElement = document.createElement('script')
  _scriptElement.async = true
  _scriptElement.onload = (): void => {
    (window as any).YaDelivery.createWidget({
      containerId: 'yandex-delivery-widget',
      params: {
        size: { height: '560px', width: '100%' },
        source_platform_station: constants.yandexDelivery.sourcePlatformStationId,
        // physical_dims_weight_gross: cartStore.weightInGrams!, // "Не удалось рассчитать сроки и стоимость".
        delivery_price: null,
        delivery_term: null,
        show_select_button: true,
        filter: {
          type: ['pickup_point'],
          payment_methods: ['already_paid'],
          payment_methods_filter: 'or'
        }
      }
    })

    document.addEventListener('YaNddWidgetPointSelected', onPickUpPointSelected)
  }
  _scriptElement.src = 'https://ndd-widget.landpro.site/widget.js'
  scriptElement.value = _scriptElement
  document.getElementsByTagName('head')[0].appendChild(_scriptElement)
}
</script>

<template>
  <CustomModalWindow v-model="model" size="large" @closed="closed" @opened="opened">
    <template #title>
      Выбор пункта выдачи заказов&nbsp;— Яндекс Доставка
    </template>

    <PickUpPointNotification class="mb-6" />

    <Preloader v-if="isGettingShippingCost" />

    <div v-show="!isGettingShippingCost" id="yandex-delivery-widget" />

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>

<style lang="scss" scoped>
#yandex-delivery-widget {
  background: #f2f2f3;
  height: 560px;
}

:deep(.ydw-widget) {
  box-sizing: border-box;
  padding: 2px;

  p {
    margin-bottom: 0 !important;
  }
}
</style>
